import * as React from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';

import { ProductDetails } from './common-event-properties';
import {
  CookiePreferences,
  useConsentCookie,
} from './cookie-banner/use-consent-cookie';
import { useStartTrackingIfOutsideGDPR } from './gdpr/gdpr';
import { TCustomData } from './tracking';
import { Events } from './tracking-events/event';
import { TrackingTool } from './tracking-tool';
import { SpecialEvents, TrackingProperties, useTracking } from './use-tracking';

type Context = {
  trackEvent: (event: {
    category: string;
    action: string;
    label: string;
    customData?: Partial<TCustomData>;
  }) => void;
  startTracking: () => void;
  stopTracking: () => void;
  consentCookie?: CookiePreferences;
  savePreferences: (preferences: CookiePreferences) => void;
  allowAll: () => void;
  trackCommonEvent: (event: Events, properties?: TrackingProperties) => void;
  specialEvents: SpecialEvents;
};

const TrackingContext = createContext<Context>({
  trackEvent: () => {},
  startTracking: () => {},
  stopTracking: () => {},
  savePreferences: () => {},
  allowAll: () => {},
  trackCommonEvent: () => {},
  specialEvents: {
    signIn: () => {},
    pageView: () => {},
    onUserCreated: () => {},
  },
});

export type RenderUserConsentDialogType = (props: {
  allowAll: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
}) => React.ReactNode;

type TrackingProviderProps = {
  children: React.ReactNode;
  trackingTools: TrackingTool[];
  productDetails: ProductDetails;
  showCookieBanner?: boolean;
  renderUserConsentDialog?: RenderUserConsentDialogType;
};

export const CommonTrackingProvider = ({
  children,
  trackingTools,
  productDetails,
  showCookieBanner,
  renderUserConsentDialog,
}: TrackingProviderProps) => {
  const {
    startTracking,
    stopTracking,
    trackEvent,
    specialEvents,
    trackCommonEvent,
  } = useTracking(trackingTools, productDetails);

  //Automatically turn on performance if consent cookie is present and has performance set.
  useEffect(() => {
    const performanceConsent = consentCookie?.performance ?? false;
    if (performanceConsent) {
      startTracking();
    }
  }, []);
  const { loading, isCoveredByGdpr } =
    useStartTrackingIfOutsideGDPR(startTracking);
  const { consentCookie, savePreferences, allowAll } = useConsentCookie({
    turnOn: startTracking,
    turnOff: stopTracking,
  });
  const contextValue = useMemo(
    () => ({
      trackEvent,
      stopTracking,
      startTracking,
      savePreferences,
      allowAll,
      consentCookie,
      trackCommonEvent,
      specialEvents,
    }),
    [
      trackEvent,
      stopTracking,
      startTracking,
      savePreferences,
      allowAll,
      consentCookie,
      trackCommonEvent,
      specialEvents,
    ],
  );

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
      {showCookieBanner &&
        !loading &&
        isCoveredByGdpr &&
        consentCookie === undefined &&
        renderUserConsentDialog &&
        renderUserConsentDialog({
          allowAll,
          savePreferences,
        })}
    </TrackingContext.Provider>
  );
};

export function useCommonTracking() {
  return useContext(TrackingContext);
}
